class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Litmus: close only on an Escape that was pressed inside the drawer. Shopify's cancellation
    // policy modal closes on keydown and hands focus back to the drawer, so the keyup landed here
    // and closed the drawer too.
    this.addEventListener('keydown', (evt) => {
      if (evt.code === 'Escape') this.escapePressedInside = true;
    });
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.escapePressedInside) this.close();
      this.escapePressedInside = false;
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.letTabReachWallets();
  }

  // Litmus: Shopify's accelerated checkout buttons (Shop Pay, Google Pay) follow "Check out"
  // inside closed shadow DOM, which trapFocus (global.js) cannot see. Tab on "Check out"
  // wrapped to the top of the drawer, so the wallets could not be reached by keyboard
  // (WCAG 2.1.1). Let that Tab continue into them, and bring focus back to the top of the
  // drawer once it moves past the last wallet. Shopify's subscription consent notice below the
  // wallets holds the "cancellation policy" control (made focusable by litmus-subscription-policy.js),
  // so it counts as part of that tail. A click there opens Shopify's policy modal outside the drawer,
  // so clicks end the tail first and focus stays in the modal.
  letTabReachWallets() {
    this.addEventListener('click', () => (this.tabbingThroughWallets = false), { capture: true });
    this.addEventListener('keydown', (event) => {
      if (event.code?.toUpperCase() !== 'TAB' || event.shiftKey || event.target.id !== 'CartDrawer-Checkout') return;
      const wallets = this.querySelector('shopify-accelerated-checkout-cart');
      if (!wallets || !wallets.getBoundingClientRect().height) return;
      // trapFocus's document listener (not run yet: this one is on the drawer) would wrap here.
      if (typeof trapFocusHandlers !== 'undefined' && trapFocusHandlers.keydown) {
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
      }
      this.tabbingThroughWallets = true;
      this.scrollBeforeWallets = window.scrollY;
    });
    document.addEventListener('focusin', (event) => {
      if (!this.tabbingThroughWallets) return;
      if (this.contains(event.target)) {
        if (!event.target.closest('shopify-accelerated-checkout-cart, #shopify-buyer-consent')) {
          this.tabbingThroughWallets = false;
        }
        return;
      }
      this.tabbingThroughWallets = false;
      if (!this.classList.contains('active')) return;
      // Focus briefly landed on the page behind the drawer, which scrolled it; put the page back.
      window.scrollTo({ top: this.scrollBeforeWallets, behavior: 'instant' });
      const first = getFocusableElements(document.getElementById('CartDrawer'))[0];
      if (first) first.focus({ preventScroll: true });
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Litmus: the drawer re-renders its status region (inside .drawer__inner) empty, so changing a
  // quantity told screen reader users nothing (WCAG 4.1.3). Announce the new total, worded like
  // the cart page's "New estimated total", once the swapped-in region is in the tree.
  updateLiveRegions(line, message) {
    super.updateLiveRegions(line, message);
    const status = document.getElementById('CartDrawer-LiveRegionText');
    const total = document.querySelector('#CartDrawer .totals__total-value');
    if (!status || !total) return;
    setTimeout(() => {
      status.textContent = `${status.dataset.totalLabel || ''}: ${total.textContent.trim()}`;
    }, 150);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
